import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CreateActionWrapper, FundDetailsController, FinaliseController } from '@Components';
import { useRouter, scrollToTop } from '@Helpers';
import { PathParams, Routes } from '@App/paths';
import { Fund } from '@Types';
import styles from './FundForm.module.scss';
import { FundCriteriaController } from '@Components/FundCriteria/FundCriteriaController';
import { useGetFundData } from '@Hooks/Api';

export const FundForm = () => {
  const { t } = useTranslation('fund');
  const { params, push, pathname } = useRouter();
  // Check for step query parameter to determine initial step
  const initialStep = params.step ? Number(params.step) : 0;
  const [currentStep, setCurrentStep] = useState(initialStep);
  const [fundId, setFundId] = useState<number | null>(Number(params[PathParams.FUND_ID]) || null);
  const [isFundConfirmed] = useState(false);
  const [getFundData, { loading: isFundDataLoading, data: fundData }] = useGetFundData();
  const [fundDetailsData, setFundDetailsData] = useState<any>();
  const [selectedFund, setSelectedFund] = useState<Fund | null>(null);

  // Check for stored purpose field when component mounts (for loading description from cloned fund)
  useEffect(() => {
    const storedPurpose = sessionStorage.getItem('fundPurpose');
    if (storedPurpose && !fundDetailsData) {
      setFundDetailsData({ purpose: storedPurpose });
      // Clear the sessionStorage after using it
      sessionStorage.removeItem('fundPurpose');
    }
  }, [fundDetailsData]);

  // Remove the step query parameter from URL after using it
  useEffect(() => {
    if (params.step) {
      push(window.location.pathname, undefined, { shallow: true });
    }
  }, [params.step, push]);

  useEffect(() => {
    if (fundId) {
      getFundData(fundId);
    }
    // We don't need to re-fetch the data we've already got when setFundId is called,
    // and we don't want to cause components further down the line to unmount due to showing the Loader,
    // so don't include fundId in the dependencies.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getFundData]);

  const nextStep = () => {
    setCurrentStep(currentStep + 1);
    scrollToTop();
  };

  const prevStep = () => {
    setCurrentStep(currentStep - 1);
    scrollToTop();
    if (fundId) {
      getFundData(fundId);
    }
  };

  const handleFundSelection = async (fund: Fund) => {
    const isDraft = fund.status === 'DRAFT';
    if (isDraft) {
      // For draft funds, reload to the edit page
      window.location.href = Routes.FUND_EDIT(fund.id);
    } else {
      // For non-draft funds, reset all form data for creating a new fund
      setFundId(null); // Clear fundId to create a new fund
      setSelectedFund(fund);

      // Store the fund description in sessionStorage so it can be loaded when the user gets sent to edit page 2
      sessionStorage.setItem('fundPurpose', fund.details.purpose || '');

      return Promise.resolve();
    }
  };

  const isCreated = !params[PathParams.FUND_ID];

  const hasMatches = fundData?.matches_count === undefined || fundData?.matches_count === 0;

  let steps = [];
  let pageHeading = [];
  let content = [];

  // If we are creating or editing a fund without options we want to add these in
  if (hasMatches || selectedFund) {
    steps.push({ label: t('steps.criteria') });
    pageHeading.push({ header: undefined, subheader: undefined });
    content.push(
      <FundCriteriaController
        fundData={selectedFund || fundData}
        fundDataLoading={isFundDataLoading}
        nextStep={nextStep}
        setFundId={setFundId}
        onFundSelection={handleFundSelection}
        isCreatingFromSelectedFund={selectedFund !== null} // Flag to indicate we're creating a new fund from selected fund
      />,
    );
  }

  steps.push(...[{ label: t('steps.details') }, { label: t('steps.finalise') }]);

  pageHeading.push(
    ...[
      { header: t('details.header'), subheader: t('details.subheader') },
      { header: t('process.header') },
    ],
  );

  content.push(
    ...[
      <FundDetailsController
        currentStep={currentStep}
        stepsCount={steps.length}
        nextStep={nextStep}
        prevStep={prevStep}
        fundId={fundId}
        fundDetailsData={fundDetailsData}
        setFundDetailsData={setFundDetailsData}
      />,
      <FinaliseController
        currentStep={currentStep}
        stepsCount={steps.length}
        prevStep={prevStep}
        fundId={fundId}
        isFundConfirmed={isFundConfirmed}
      />,
    ],
  );

  return (
    <CreateActionWrapper
      steps={steps}
      currentStep={currentStep}
      {...pageHeading[currentStep]}
      childrenWrapperClassName={
        (isCreated || !isFundConfirmed) && currentStep === 0 ? styles.contentWrapper : ''
      }
    >
      {content[currentStep]}
    </CreateActionWrapper>
  );
};
